import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { 
	withStyles,
	Button,
	Grid,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	Link,
	Typography,
	MenuItem,
	Tooltip
} from '@material-ui/core';
import classNames from 'classnames';
import { Scrollbars } from 'react-custom-scrollbars';
import { doesUserExist, isDuplicateEntry } from '../../utils/users';
import { isChrome } from '../../utils/browser';
import { hideModal, resetModal } from 'redux/modules/modal';
import { inviteUserAdd, inviteUserUpdate, inviteUserRemove, inviteUserSetMessage } from 'redux/modules/companies';
import { Select } from '../../components/Features/components';
import Add from '@material-ui/icons/Add';
import SelectArrow from '@material-ui/icons/KeyboardArrowDown';
import { IconInvite, Info } from '../../theme/icons';
import { emailValidationPattern } from '../../theme/uiConfig';
import PropTypes from 'prop-types';
import FormField from '../FormField';
import { XIcon, SpinnerIcon } from '../../theme/icons';
import { clearInvitedUsers, getInvitedUsers, getInviteMessage, clearInviteMessage } from '../../utils/storage';
import _ from 'lodash';
import ERRORS from '../../errors/errorids';
import { USER_ROLES, USER_ROLE_ADMIN, USER_ROLE_BASIC, USER_ROLE_GUEST } from '../../config';
import { trackAddUsersPopupShown } from 'lib/Segment/customTrackers';
import { getTrackingOptions } from 'lib/Segment/helpers';

import i18n from 'i18n/i18n';
const localize = i18n.loc.localize;

const PERMISSION_ROLE = [
	{ key: USER_ROLE_ADMIN, value: USER_ROLES[USER_ROLE_ADMIN].label },
	{ key: USER_ROLE_BASIC, value: USER_ROLES[USER_ROLE_BASIC].label },
	// Temporarily enable users to add guests through this popup - users can activate perpetual version as guests
	// without needing to upgrade to team
	{ key: USER_ROLE_GUEST, value: USER_ROLES[USER_ROLE_GUEST].label },
];

const ERROR_MESSAGES = {
	INVALID_EMAIL: localize('users-email-error-invalid'),
	USER_ALREADY_EXISTS: localize('users-email-error-exists'),
	USER_ALREADY_EXISTS_IN_INVITE_LIST: localize('users-email-already-exists-in-invite-list'),
	USER_MESSAGE_EMPTY: localize('users-message-empty')
};

const errors = {
	[ERRORS.API_REQUEST_LIMIT_EXCEEDED] : localize('error-cb-api-rate-limit-reached'),
	[ERRORS.OFFLINE]: localize('users-batch-add-error-offline'),
	[ERRORS.SUBSCRIPTION_PLAN_PROHIBITS_USER_MODIFICATION]: localize('users-batch-add-error-cant-modify-users'),
	[ERRORS.FAILED_TO_ADD_USERS_TO_COMPANY]: localize('users-batch-add-error-failed-to-add-user-to-company'),
	[ERRORS.INVALID_USER]: localize('users-batch-add-error-invalid-user'),
	[ERRORS.INVALID_USERS]: localize('users-batch-add-error-invalid-users'),
	[ERRORS.USER_ALREADY_EXISTS_IN_COMPANY]: localize('users-batch-add-error-user-already-exists')
};

const EMPTY_USER_MEMBER = { email: '', role: USER_ROLE_BASIC, id: null };
const EMPTY_USER_GUEST = { email: '', role: USER_ROLE_GUEST, id: null };

const styles = theme => {
	return {
		/* Dialog */
		dialogPaper: {
			minWidth: 444
		},
		dialogWrapper: {
			position: 'relative',
			padding: '15px 10px 0',
			minHeight: 291
		},
		dialogTitle: {
			'& h6': {
				fontWeight: 600,
				fontSize: '28px',
				lineHeight: 1.4,
				marginBottom: 20,
				textAlign: 'left'
			}
		},
		/* dialog content */
		dialogContent: {
			overflow: 'visible'
		},		
		/* close button */
		closeDialog: {
			position: 'absolute',
			top: 10,
			right: 10
		},
		closeDialogIcon: {
			color: '#6F99BD',
			width: '20px',
			height: '20px'
		},
		/* loading icon */
		loadingContainer: {
			minHeight: 402,
			position: 'relative'
		},
		loadingContent: {
			position: 'absolute',
			top: '50%',
			left: '50%',
			transform: 'translate(-50%, -50%)'
		},
		loadingMessage: {
			fontSize: 16,
			lineHeight: 1.5,
			opacity: 1
		},
		loadingIcon: {
			display: 'block',
			height: '24px',
			width: '24px',
			margin: '0 auto 10px',
			fill: '#96DCFD',
			animation: 'spin 2s linear infinite'
		},
		'@keyframes spin': {
			'0': {
				transform: 'rotate(0deg)'
			},
			'100%': {
				transform: 'rotate(360deg)'
			}
		},			
		/* input field */
		field: {
			width: '100%',
			margin: 0,

			'& input, & textarea': {
				border: '1px solid #aabfd1',
				borderStyle: 'solid',
				borderRadius: 5,
				color: theme.palette.text.primary,
				padding: '10px',
				'&:focus, &:hover': {
					color:'#2888c3',
					borderColor:'#2888c3'
				},

				'&::placeholder': {
					fontFamily: 'Open Sans, sans-serif',
					fontWeight: 'normal',
					fontStyle: 'normal',
					fontSize: '14px',
					lineHeight: '19px',
					color: theme.palette.text.primary
				}
			},

			'& textarea': {
				width: '425px',
				display: 'block',
			},

			'& [id*=helper-text]': {
				paddingLeft: 0,
				color: theme.palette.error.main
			},

			'& [class*=error] input': {
				borderColor: `${theme.palette.error.main} !important`
			}
		},
		/* buttons */
		button: {
			fontFamily: 'Open Sans, sans-serif',
			borderRadius: 5,
			fontWeight: 600,
			fontSize: 18,
			lineHeight: 1.3,
			opacity: '1 !important'
		},
		buttonPrimary: {
			background: theme.palette.primary.main,
			color: 'white',
			textShadow: 'none',
			paddingLeft: 10,
			paddingRight: 15,
			'&[class*=contained][class*=disabled]': {
				backgroundColor: '#f4f4f4',
				color: '#4a5660'
			}
		},
		/* upgrade info message */
		upgradeInfoMessage:  {
			color: theme.palette.error.main,
			fontSize: 14,
			lineHeight: 1.5,
			opacity: 1,
			textAlign: 'left',
			marginTop: -15,
			marginBottom: 15,
			marginRight: 20,
			'& a': {
				color: theme.palette.primary.main
			}
		},
		/* error message */
		errorMessage:  {
			color: theme.palette.error.main,
			fontSize: 14,
			lineHeight: 1.5,
			opacity: 1,
			textAlign: 'left',
			marginTop: -15,
			marginBottom: 15,
			'& a': {
				color: theme.palette.primary.main
			}
		},
		/* inviation message */
		invitationEmailLabelContainer: {
			width: '100%',
			paddingBottom: 40
		},
		invitationEmailLabel: {
			textAlign: 'left',
			color: theme.palette.text.primary,
			opacity: 1,
			margin: '0 0 5px'
		},
		inviteButtonContainer: {
			position: 'relative',
			width: 'calc(100% + 60px)',
			margin: '0 0 0 -30px',
			padding: '24px 30px 10px 0',
			'&:before': {
				content: '""',
				position: 'absolute',
				top: 0,
				left: 0,
				width: '100%',
				height: 'calc(100% + 14px)',
				boxShadow: '0px -4px 10px 0px rgba(0, 0, 0, 0.1)'
			}
		},
		inviteUsers: {
		},
		iconInviteUsers: {
			marginRight: 5
		},
		/* user row */
		userRow: {
			marginBottom: 16,
			width: '100%'
		},
		/* remove user */
		removeUserColumn: {
			width: 33
		},
		removeUserButon: {
			position: 'relative',
			left: -9,
			top: 5,
			'&[class*=disabled]': {
				opacity: 0.5
			}
		},
		removeUserIcon: {
			color: '#6F99BD',
			width: 15,
			height: 15,
			'&:hover': {
				color: '#2888c3'
			}
		},
		/* user email */
		userEmailColumn: {
			width: 238,
			paddingRight: 16
		},
		/* user role */
		userRoleColumn: {
			paddingRight: 10
		},
		/* user info */
		userInfoColumn: {
			width: 19
		},
		/* add another user */
		addAnotherUserContainer: {
			width: '100%',
			textAlign: 'left'
		},
		addAnotherUserLink: {
			display: 'inline-block',
			margin: '0 0 40px 0',
			textAlign: 'left',
			color: theme.palette.colors.alpha,
			'&:hover, &:hover svg': {
				color: '#2888c3'
			},
			fontSize: '16px',
			lineHeight: '20px',
			fontWeight: 600
		},
		addAnotherUserIconButton: {
			margin: '0 0 0 -6px',
		},
		addAnotherUserIcon: {
			width: 20,
			height: 20
		},
		/* roleSelect */
		roleSelect: {
			width: 125
		},
		selectElement: {
			borderRadius: 5,
			boxShadow: 'none',
			borderColor: '#aabfd1',
			paddingRight: 10,
			paddingLeft: 10,
			textAlign: 'left',
			'&:hover, &:focus': {
				borderRadius: 5,
				color:'#2888c3',
				borderColor:'#2888c3'
			}
		},
		selectElementItem: {
			'&:hover': {
				background: '#e0f5fe'
			},
			'&[class*=selected]': {
				background: '#e0f5fe !important'
			}
		},
		selectElementIcon: {
			right: 4,
			'&:hover': {
				color:'#93c3e1'
			}			
		},
		selectElementPaper: {
			borderRadius: 5,
			border: 0,
			boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.1)',
		},
		/* info icon */
		infoIconButton: {
			position: 'relative',
			top: 8,
			width: 16,
			height: 16,
			color: theme.palette.colors.white,
			backgroundColor: '#6F99BD',
			borderColor: '#6F99BD',
			padding: 0,
			'&:hover': {
				backgroundColor: `${theme.palette.colors.epsilonHover} !important`,
				borderColor: `${theme.palette.colors.epsilonHover} !important`
			}
		},
		tooltipArrow: {
			position: 'absolute',
			fontSize: 6,
			width: '3em',
			height: '1em',
			bottom: 0,
			left: '50%',
			transform: 'translateX(-50%)',
			marginBottom: '-0.95em',
			'&::before': {
				content: '""',
				margin: 'auto',
				display: 'block',
				width: 0,
				height: 0,
				borderStyle: 'solid',
				borderWidth: '1em 1em 0 1em',
				borderColor: `${theme.palette.common.black} transparent transparent transparent`,
			},
		},
		infoTooltip: {
			padding: 7,
			backgroundColor: theme.palette.common.black,
		},
		infoTooltipText: {
			color: theme.palette.common.white,
			opacity: 1
		}
	};
};

@withStyles(styles)
class InviteUserModal extends Component {

	constructor(props) {
		super(props);

		this.state = {
			xhrInProgress: false,
			apiValidationError: false,
			willIncreaseUserNumber: false,
			errors: {},
		};

		this.handleAddAnotherUser = this.handleAddAnotherUser.bind(this);
		this.scrollbarRef = React.createRef(null);

		/* get users customer previously entered to Invite Users popup before he was redirected to CB checkout */
		const usersFromStorage = getInvitedUsers();
		if (usersFromStorage) {
			usersFromStorage.forEach(user => this.props.inviteUserAdd(user));
			clearInvitedUsers();
		} else if (this.props.companyInviteUsers.length === 0) {
			const defaultUser = this.getUserPlaceholder(props.email);
			this.props.inviteUserAdd(defaultUser);
		}

		const inviteMessage = getInviteMessage();
		if (inviteMessage) {
			this.props.inviteUserSetMessage(inviteMessage);
			clearInviteMessage();
		}
	}

	render() {
		const { modalShown, resetModal, classes } = this.props;

		return (
			<Dialog
				open={modalShown}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
				onExited={resetModal}
				BackdropProps={{ style: { backgroundColor: 'rgba(46, 63, 79, 0.4)' } }}
				classes={{ paper: classes.dialogPaper }}
				PaperProps={{
					style: {
						border: 0,
						width: 309,
						minWidth: 505,
						borderRadius: 5,
						boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.08)'
					}
				}}
			>
				<div className={classes.dialogWrapper}>
					{!this.state.xhrInProgress && (
						<IconButton
							className={classes.closeDialog}
							component='span'
							aria-label='Close'
							onClick={() => this.closeDialog()}
						>
							<XIcon className={classes.closeDialogIcon} />
						</IconButton>
					)}
					
					{(() => {
						if (this.state.xhrInProgress) {
							return this.renderLoading();
						} else {
							return this.renderForm();
						}
					})()}
				</div>
			</Dialog>
		);
	}

	closeDialog() {
		this.props.hideModal();
	}

	renderLoading() {
		const { classes } = this.props;
		return (
			<div className={classes.loadingContainer}>
				<div className={classes.loadingContent}>
					<SpinnerIcon className={classes.loadingIcon} />
					<Typography className={classes.loadingMessage} variant="body2">
						{localize('users-popup-saving-users')}
					</Typography>
				</div>
			</div>
		);
	}

	renderUpgradePlanInfo() {
		const { classes } = this.props;
		return (
			<Typography className={classes.upgradeInfoMessage} variant="body2">
				{this.props.upgradeInfoMessage}
			</Typography>
		);			
	}	

	renderErrorMessage() {
		const errors = this.getErrors();
		const apiError = _.get(errors, 'apiError');
		const formError = this.hasFormError(errors) && localize('users-please-errors-found-in-the-form');
		
		let errorMessage;
		if (formError) {
			errorMessage = formError;
		} else if (apiError) {
			errorMessage = apiError;
		}

		const { classes } = this.props;
		return (
			<Typography className={classes.errorMessage} variant="body2" dangerouslySetInnerHTML={{ __html: errorMessage }} />
		);		
	}

	renderForm() {
		const { classes, companyInviteMessage } = this.props;

		return (
			<Fragment>
				<DialogTitle
					variant='body1'
					color='default'
					className={classes.dialogTitle}>{localize('users-popup-header')}
				</DialogTitle>
				<DialogContent className={classes.dialogContent} >
					<Grid container direction="column">
						{this.props.upgradeInfoMessage && this.renderUpgradePlanInfo()}
						{Object.keys(this.state.errors).length > 0 && this.renderErrorMessage()}
						<Grid container item direction="column" className={classes.userRowsContainer}>
							{this.renderUserFields()}
						</Grid>
						<Grid item className={classes.addAnotherUserContainer}>
							<Link 
								variant='body1'
								onClick={this.handleAddAnotherUser}
								className={classes.addAnotherUserLink}
								underline="none">
								<IconButton
									className={classes.addAnotherUserIconButton}
									color='primary'
									component='span'
									disableRipple>
									<Add
										style={{ fontSize: 27 }}
										className={classNames({
											[classes.addAnotherUserIcon]: true,
											['addAnotherUserIcon']: true
										})}			
									/>
								</IconButton>								
								{localize('users-popup-add-another')}
							</Link>
						</Grid>										
						<Grid item className={classes.invitationEmailLabelContainer}>
							<Typography className={classes.invitationEmailLabel} variant="body2">
								{localize('users-popup-invitation-email-message-label')}
							</Typography>
							{this.textField(
									'invitationMessage',
									companyInviteMessage,
									localize('users-popup-invitation-email-message-value'),
									null,
									null,
									null,
									true
							)}
						</Grid>							
						<Grid item container direction="row" justify="flex-end" className={classes.inviteButtonContainer}>
							<Button
								variant='contained'
								color='primary'
								data-tid="button-user-invite"
								className={classNames(classes.button, classes.inviteUsers, classes.buttonPrimary)}
								onClick={() => this.submitForm()}
								disabled={!this.isSubmitEnabled()}>
								<IconInvite className={classes.iconInviteUsers} />
								{localize('users-popup-invite-all')}
							</Button>
						</Grid>
					</Grid>
				</DialogContent>
			</Fragment>
		);
	}

	renderUserFields() {
		const { classes, companyInviteUsers } = this.props;

		const items = companyInviteUsers.map(user => {
			return (
				<Grid item key={`invite-user-${user.id}`} className={classes.userRow}>
					<Grid container direction="row">
						<Grid item className={classes.removeUserColumn}>
							<IconButton
								className={classes.removeUserButon}
								disableRipple
								component='span'
								disabled={companyInviteUsers.length === 1}
								aria-label='Close'
								onClick={() => this.removeUser(user.id)}
							>
								<XIcon className={classes.removeUserIcon} />
							</IconButton>						
						</Grid>
						<Grid item className={classes.userEmailColumn}>
							{this.textField(
								`userEmail_${user.id}`,
								user.email,
								localize('users-popup-new-user-email-address'),
								false,
								'email')}
						</Grid>
						<Grid item className={classes.userRoleColumn}>
							{this.selectField(
								`userRole_${user.id}`,
								user.role)}
						</Grid>
						<Grid item className={classes.userInfoColumn}>
							<Tooltip
								placement="top"
								title={
									<React.Fragment>
										<Typography variant='body2' className={classNames(classes.infoTooltipText)}>
											{this.renderTooltipForRole(user.role)}
										</Typography>					
										<span className={classes.tooltipArrow} />
									</React.Fragment>
								}
								classes={{
									tooltip: classes.infoTooltip
								}}
							>
								<IconButton
									className={classes.infoIconButton}
									color='primary'
									component='span'
									disableRipple
								>
									<Info />
								</IconButton>
							</Tooltip>
						</Grid>
					</Grid>
				</Grid>				
			);	
		});

		return companyInviteUsers.length > 3
			?
			(
				<Scrollbars
					ref={this.scrollbarRef}
					style={{ height:215 }}
					autoHide={false}
					renderThumbVertical={({ style, ...props }) =>
						(<div
							{...props}
							style={{
								...style,
								backgroundColor: '#6F99BD',
								opacity: 0.5,
								borderRadius: 3
							}}/>)
					}
				>{items}</Scrollbars>				
			)
			:
			items;
	}

	isSubmitEnabled() {
		if (this.props.companyInviteUsers.length === 0) return;
		// check for input error
		return Object.keys(this.getErrors()).length === 0 && !this.props.companyInviteUsers.some(user => user.email === '') || 
		// check for api error
		Object.keys(this.getErrors()).length === 1 && _.get(this.state, 'errors.apiError');
	}

	getErrors() {
		const errorState = this.state.errors;
		Object.keys(errorState).forEach(fieldName => {
			if (fieldName.indexOf('userEmail') !== -1) {
				const userId = parseInt(fieldName.split('_')[1]);
				const userFound = this.props.companyInviteUsers.find(user => user.id === userId);
				if (!userFound) {
					delete errorState[fieldName];
				}
			}
		});
		return errorState;
	}

	hasFormError(errorState) {
		let result = false;

		Object.keys(errorState).forEach(fieldName => {
			if (fieldName.indexOf('invitationMessage') !== -1 || fieldName.indexOf('userEmail') !== -1) {
				result = true;
			}
		});

		return result;
	}

	componentDidMount() {
		trackAddUsersPopupShown(getTrackingOptions(this.props.company));
		this.validateAll(null, null, true);
		if (this.props.showApiError && (this.props.companies.estimateCostOnAddCompanyUsersErrorId ||
			this.props.companies.addCompanyUsersErrorId)) {
			this.handleApiErrors(this.props);
		}
	}

	componentDidUpdate() {
		if (this.state.willIncreaseUserNumber) {
			if (this.scrollbarRef && this.scrollbarRef.current) {
				this.scrollbarRef.current.scrollToBottom();
			}
		}
	}

	componentWillReceiveProps(nextProps) {
		const prevIds = this.props.companyInviteUsers.map(item => item.id);
		const newdIds = nextProps.companyInviteUsers.map(item => item.id);

		if (nextProps.companyInviteUsers.length && JSON.stringify(prevIds) !== JSON.stringify(newdIds)
			|| nextProps.companyInviteMessage && this.state.errors['invitationMessage']) {
			this.validateAll(nextProps.companies.inviteUsers, nextProps.companyInviteMessage);
		}

		this.setState({
			willIncreaseUserNumber: nextProps.companyInviteUsers.length > this.props.companyInviteUsers.length
		});

		if (this.props.companies.estimateCostOnAddCompanyUsersErrorId !== 
			nextProps.companies.estimateCostOnAddCompanyUsersErrorId ||
			this.props.companies.addCompanyUsersErrorId !== nextProps.companies.addCompanyUsersErrorId) {
			this.handleApiErrors(nextProps);
		}
	}

	handleApiErrors(propsSource) {
		if (propsSource.companies.estimateCostOnAddCompanyUsersErrorId || propsSource.companies.addCompanyUsersErrorId) {
			let errorMessage;
			errorMessage = propsSource.companies.estimateCostOnAddCompanyUsersErrorId &&
				errors[propsSource.companies.estimateCostOnAddCompanyUsersErrorId] || 
				propsSource.companies.addCompanyUsersErrorId &&
					errors[propsSource.companies.addCompanyUsersErrorId] ||
				localize('users-batch-add-error-unknown');

			this.setState({
				xhrInProgress: false,
				apiValidationError: true,
				errors: { apiError: errorMessage }
			});
		}
	}	

	textField(name, value, placeholder, disabled, type, maxLength, autosize) {
		const { classes } = this.props;
		
		return (
			<FormField
				value={value}
				autoComplete={isChrome ? 'disabled' : 'off'}
				className={classes.field}
				data-tid={`form-user-${name}`}
				onChange={event => this.onTextFieldChange(event, name)}
				onBlur={event => this.onTextFieldBlur(event, name)}
				onKeyPress={event => this.onKeyPress(event, name)}
				id={name}
				placeholder={placeholder}
				{...(type ? { type } : {})}
				{...(autosize ? { autosize } : {})}
				margin="normal"
				helperText={this.state.errors[name]}
				error={!!this.state.errors[name]}
				disabled={disabled}
				maxLength={maxLength}
				inputRef={this[name]}
			/>
		);
	}

	selectField(name, role) {
		const { classes } = this.props;

		let availableRoles;
		
		if (this.props.allowedRoles) {
			availableRoles = PERMISSION_ROLE.filter(item => this.props.allowedRoles.includes(item.key));
		} else {
			availableRoles = PERMISSION_ROLE;
		}

		return (
			<Select
				id={name}
				data-tid={name}
				name={name}
				onChange={(event) => this.onSelectFieldChange(event, name)}
				className={classes.roleSelect}
				disableUnderline
				fullWidth
				classes={{ select: classes.selectElement, icon: classes.selectElementIcon }}
				MenuProps={{
					classes: {
						paper: classes.selectElementPaper
					}
				}}		
				IconComponent={SelectArrow}
				value={role}
			>
				{availableRoles.map(option => (
					<MenuItem key={option.key} value={option.key} className={classes.selectElementItem}>
						{option.value}
					</MenuItem>
				))}
			</Select>			
		);
	}

	onTextFieldChange(event, name) {
		if (name.indexOf('userEmail') !== -1) {
			const userId = parseInt(name.split('_')[1]);
			const relatedUser = this.props.companyInviteUsers.find(user => user.id === userId);
			relatedUser.email = event.target.value;
			this.props.inviteUserUpdate(relatedUser);
			this.state.errors[name] && this.validateEmail(name, event.target.value);

		} else if (name.indexOf('invitationMessage') !== -1) {
			this.props.inviteUserSetMessage(event.target.value);
			this.state.errors[name] && this.validateMessage(name, event.target.value);
		}
	}

	validateAll(users, message, isPopupLoad) {
		const messageToValidate = message || this.props.companyInviteMessage;
		messageToValidate && this.validateMessage('invitationMessage', messageToValidate);

		const usersToValidate = users || this.props.companyInviteUsers;
		
		if (usersToValidate.length === 1 && usersToValidate[0].email === '') return;
		usersToValidate.forEach((user, index) => {
			if (index === usersToValidate.length-1 && user.email === '' && !isPopupLoad) return;
			this.validateEmail(`userEmail_${user.id}`, user.email, usersToValidate);
		});
	}

	onTextFieldBlur(event, name) {
		if (name.indexOf('userEmail') !== -1) {
			this.validateEmail(name, event.target.value);
		} else if (name.indexOf('invitationMessage') !== -1) {
			this.validateMessage(name, event.target.value);
		}
	}	

	validateEmail(name, value, companyInviteUsers) {
		const { rawDataById, selectedId } = this.props.companies;
		const company = rawDataById[selectedId];
		const { users } = company;
		const userId = parseInt(name.split('_')[1]);

		const companyInviteUsersToValidate = companyInviteUsers || this.props.companyInviteUsers;

		let error;
		if (!emailValidationPattern.test(value)) {
			error = ERROR_MESSAGES.INVALID_EMAIL;
		} else if (doesUserExist(users, value)) {
			error = ERROR_MESSAGES.USER_ALREADY_EXISTS;
		} else if (isDuplicateEntry(companyInviteUsersToValidate, value, userId)) {
			error = ERROR_MESSAGES.USER_ALREADY_EXISTS_IN_INVITE_LIST;
		}

		let errorState = this.getErrors();
		if (error) {
			errorState[name] = error;
		} else {
			errorState[name] && (delete errorState[name]);
		}

		this.setState({ errors: errorState });
	}

	validateMessage(name, value) {
		const errorState = this.getErrors();

		let error = value && value.replace(/\s+/g, '').length === 0
			?
			ERROR_MESSAGES.USER_MESSAGE_EMPTY
			:
			'';

		if (error) {
			errorState[name] = error;
		} else {
			errorState[name] && (delete errorState[name]);
		}

		this.setState({ errors: errorState });		
	}

	onSelectFieldChange(event, name) {
		if (name.indexOf('userRole') !== -1) {
			const userId = parseInt(name.split('_')[1]);
			const relatedUser = this.props.companyInviteUsers.find(user => user.id === userId);
			relatedUser.role = event.target.value;
			this.props.inviteUserUpdate(relatedUser);
		}		
	}	

	setFieldState(fieldName, value) {
		this.setState({ [fieldName]: value });
	}

	onKeyPress(event, name) {
		if (!this.isSubmitEnabled() || name === 'invitationMessage') return;
		if (event.key === 'Enter') {
			this.submitForm();
		}
	}

	submitForm() {
		this.setState({ xhrInProgress: true, apiValidationError: false });
		this.props.handleAddUsers(this.props.companyInviteUsers, this.props.companyInviteMessage);
	}

	handleAddAnotherUser() {
		this.props.inviteUserAdd(this.getUserPlaceholder());
	}

	removeUser(id) {
		this.props.inviteUserRemove(id);
	}

	getUserPlaceholder(email) {
		let defaultUser;
		if (this.props.allowedRoles) {
			if (this.props.allowedRoles.includes(USER_ROLE_BASIC)) {
				defaultUser = _.clone(EMPTY_USER_MEMBER);
			} else if (this.props.allowedRoles.includes(USER_ROLE_GUEST)) {
				defaultUser = _.clone(EMPTY_USER_GUEST);
			}
		} else {
			defaultUser = _.clone(EMPTY_USER_MEMBER);
		}

		email && (defaultUser.email = email);
		defaultUser.id = new Date().getTime();
		return defaultUser;
	}

	renderTooltipForRole(role) {
		if (role === USER_ROLE_ADMIN) {
			return localize('users-roles-popover-admin-description');
		} else if (role === USER_ROLE_BASIC) {
			return localize('users-roles-popover-member-description');
		} else if (role === USER_ROLE_GUEST) {
			return localize('users-roles-popover-guest-description');
		}
	}
}

InviteUserModal.propTypes = {
	companies: PropTypes.object,
	modalShown: PropTypes.bool,
	hideModal: PropTypes.func,
	resetModal: PropTypes.func,
	classes: PropTypes.object,
	email: PropTypes.string,
	inviteUserAdd: PropTypes.func,
	inviteUserUpdate: PropTypes.func,
	inviteUserRemove: PropTypes.func,
	inviteUserSetMessage: PropTypes.func,
	companyInviteUsers: PropTypes.array,
	companyInviteMessage: PropTypes.string,
	handleAddUsers: PropTypes.func,
	showApiError: PropTypes.bool,
	company: PropTypes.object,
	allowedRoles: PropTypes.array,
	upgradeInfoMessage: PropTypes.string,
};

const mapStateToProps = state => ({
	company: state.companies.rawDataById ? state.companies.rawDataById[state.companies.selectedId] : null,
	modalShown: state.modal.modalShown,
	companies: state.companies,
	companyInviteUsers: state.companies.inviteUsers,
	companyInviteMessage: state.companies.inviteUsersMessage
});

const mapDispatchToProps = dispatch => bindActionCreators({
	hideModal,
	resetModal,
	inviteUserAdd,
	inviteUserUpdate,
	inviteUserRemove,
	inviteUserSetMessage
}, dispatch);

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(InviteUserModal);


// WEBPACK FOOTER //
// ./src/components/Modal/InviteUsersModal.js